import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SnippetsOutlined
} from "@ant-design/icons"
import { App, Button, Card, Flex, Tag, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../components/EntityTable/surfaces.ts"
import { SearchBar } from "../../../../components/Input/SearchBar.tsx"
import { empty } from "../../../../constants/placeholders.ts"
import { SCROLL_PAGE_SIZE } from "../../../../redux/shared/paged-list.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import * as services from "../../api/services.ts"
import type { SupplierMaterialData } from "../../api/services.types.ts"
import {
  formatMaterialMargin,
  materialUnitMargin
} from "../../helpers/material-margin.ts"
import type { SupplierMaterial } from "../../types/types.ts"
import { MaterialPasteModal } from "./MaterialPasteModal.tsx"
import { SupplierMaterialEditRow } from "./SupplierMaterialEditRow.tsx"

const { Text } = Typography

const NEW_ROW_ID = "__new_material__"

const blankMaterial = (): SupplierMaterial => ({
  id: NEW_ROW_ID,
  name: "",
  unit: null,
  unit_cost: null,
  unit_sale_price: "",
  created: "",
  updated: ""
})

interface SupplierMaterialsCardProps {
  supplierId: string
  supplierName: string
  canEdit: boolean
  canDelete: boolean
}

export const SupplierMaterialsCard = ({
  supplierId,
  supplierName,
  canEdit,
  canDelete
}: SupplierMaterialsCardProps) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()

  const [materials, setMaterials] = useState<SupplierMaterial[]>([])
  const [count, setCount] = useState(0)
  const [nextPage, setNextPage] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [fetching, setFetching] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [search, setSearch] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [pasting, setPasting] = useState(false)

  // A search fired while an earlier page is still in flight answers second
  // often enough to matter: only the newest request may write.
  const requestRef = useRef(0)

  const loadPage = useCallback(
    async (wanted: number, { append }: { append: boolean }) => {
      const requestId = requestRef.current + 1
      requestRef.current = requestId

      setFetching(true)
      const response = await services.getSupplierMaterials({
        supplier: supplierId,
        q: search,
        page: wanted,
        page_size: SCROLL_PAGE_SIZE
      })
      if (requestRef.current !== requestId) return

      setFetching(false)
      if (!response.ok) {
        setLoadFailed(true)
        return
      }

      const data = response.data
      setLoadFailed(false)
      setPage(wanted)
      setCount(data?.count ?? 0)
      setNextPage(data?.next ?? null)
      setMaterials((current) =>
        append ? [...current, ...(data?.results ?? [])] : (data?.results ?? [])
      )
    },
    [supplierId, search]
  )

  useEffect(() => {
    // A narrower result set has its own first page, and a row held open for
    // edit is not in it.
    setEditingId(null)
    setIsAdding(false)
    void loadPage(1, { append: false })
  }, [loadPage, reloadKey])

  const refresh = useCallback(() => setReloadKey((key) => key + 1), [])

  const handleCreate = useCallback(
    async (data: SupplierMaterialData) => {
      const response = await services.createSupplierMaterial({
        supplier: supplierId,
        ...data
      })
      if (!response.ok) {
        showResponseMessage(response, t("supplierMaterials.saveFailed"))
        return
      }
      showResponseMessage(response, t("supplierMaterials.created"))
      setIsAdding(false)
      refresh()
    },
    [supplierId, refresh, t]
  )

  const handleUpdate = useCallback(
    async (id: string, data: SupplierMaterialData) => {
      const response = await services.updateSupplierMaterial({ id, ...data })
      if (!response.ok) {
        showResponseMessage(response, t("supplierMaterials.saveFailed"))
        return
      }
      showResponseMessage(response, t("supplierMaterials.updated"))
      setEditingId(null)
      refresh()
    },
    [refresh, t]
  )

  const handleDelete = useCallback(
    (record: SupplierMaterial) => {
      modal.confirm({
        centered: true,
        title: t("supplierMaterials.deleteTitle"),
        content: t("supplierMaterials.deleteConfirm", { name: record.name }),
        okText: t("supplierMaterials.actions.delete"),
        okButtonProps: { danger: true },
        onOk: async () => {
          setDeletingId(record.id)
          try {
            const response = await services.deleteSupplierMaterial(record.id)
            showResponseMessage(
              response,
              response.ok
                ? t("supplierMaterials.deleted")
                : t("supplierMaterials.deleteFailed")
            )
            if (response.ok) refresh()
          } finally {
            setDeletingId(null)
          }
        }
      })
    },
    [modal, refresh, t]
  )

  const dataSource = useMemo(
    () => (isAdding ? [blankMaterial(), ...materials] : materials),
    [materials, isAdding]
  )

  const isLocked = !!editingId || isAdding

  const columns = useMemo<ColumnsType<SupplierMaterial>>(
    () => [
      {
        title: t("supplierMaterials.columns.name"),
        dataIndex: "name",
        key: "name",
        align: "left"
      },
      {
        title: t("supplierMaterials.columns.unit"),
        dataIndex: "unit",
        key: "unit",
        width: 110,
        render: (value: string | null) => value || empty
      },
      {
        title: t("supplierMaterials.columns.unitCost"),
        dataIndex: "unit_cost",
        key: "unit_cost",
        width: 140,
        align: "right",
        render: (value: string | null) =>
          value === null ? (
            <Text type="secondary">{empty}</Text>
          ) : (
            budgetFormat(value, { minimumFractionDigits: 2 })
          )
      },
      {
        title: t("supplierMaterials.columns.unitSalePrice"),
        dataIndex: "unit_sale_price",
        key: "unit_sale_price",
        width: 160,
        align: "right",
        render: (value: string) => (
          <Text strong>
            {budgetFormat(value, { minimumFractionDigits: 2 })}
          </Text>
        )
      },
      {
        title: t("supplierMaterials.columns.margin"),
        key: "margin",
        width: 130,
        align: "right",
        render: (_value: unknown, record: SupplierMaterial) => {
          const margin = materialUnitMargin(
            record.unit_cost,
            record.unit_sale_price
          )
          if (margin === null) {
            return record.unit_cost === null ? (
              <Text type="warning">{t("supplierMaterials.passThrough")}</Text>
            ) : (
              <Text type="secondary">{empty}</Text>
            )
          }
          return (
            <Text type={margin < 0 ? "danger" : "success"}>
              {formatMaterialMargin(margin)}
            </Text>
          )
        }
      },
      ...(canEdit || canDelete
        ? [
            {
              title: t("supplierMaterials.columns.actions"),
              key: "actions",
              width: 96,
              align: "center" as const,
              render: (_value: unknown, record: SupplierMaterial) => (
                <Flex gap={4} justify="center">
                  {canEdit && (
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      disabled={isLocked || !!deletingId}
                      onClick={() => {
                        setIsAdding(false)
                        setEditingId(record.id)
                      }}
                      aria-label={t("supplierMaterials.actions.editNamed", {
                        name: record.name
                      })}
                    />
                  )}
                  {canDelete && (
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={deletingId === record.id}
                      disabled={isLocked}
                      onClick={() => handleDelete(record)}
                      aria-label={t("supplierMaterials.actions.deleteNamed", {
                        name: record.name
                      })}
                    />
                  )}
                </Flex>
              )
            }
          ]
        : [])
    ],
    [canEdit, canDelete, deletingId, handleDelete, isLocked, t]
  )

  const Row = useCallback(
    (
      props: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLTableRowElement>,
        HTMLTableRowElement
      >
    ) => {
      const rowKey = (props as { ["data-row-key"]?: string })["data-row-key"]
      const record = rowKey
        ? dataSource.find((item) => item.id === rowKey)
        : null

      if (!record) return <tr {...props} />

      const isCreating = record.id === NEW_ROW_ID
      if (!isCreating && editingId !== record.id) return <tr {...props} />

      return (
        <SupplierMaterialEditRow
          {...props}
          record={record}
          mode={isCreating ? "create" : "edit"}
          onCommit={(data) =>
            isCreating ? handleCreate(data) : handleUpdate(record.id, data)
          }
          onCancel={() =>
            isCreating ? setIsAdding(false) : setEditingId(null)
          }
        />
      )
    },
    [dataSource, editingId, handleCreate, handleUpdate]
  )

  return (
    <Card
      size="small"
      title={
        <Flex align="center" gap={8}>
          <span>{t("supplierMaterials.title")}</span>
          <Tag>{t("supplierMaterials.itemCount", { count })}</Tag>
        </Flex>
      }
      extra={
        <Flex align="center" gap={8} wrap>
          <SearchBar
            placeholder={t("supplierMaterials.searchPlaceholder")}
            defaultValue=""
            onSearch={setSearch}
            style={{ minWidth: 200, maxWidth: 260 }}
          />
          {canEdit && (
            <>
              <Button
                icon={<SnippetsOutlined />}
                disabled={isLocked}
                onClick={() => setPasting(true)}
              >
                {t("supplierMaterials.pasteButton")}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={isLocked}
                onClick={() => {
                  setEditingId(null)
                  setIsAdding(true)
                }}
              >
                {t("supplierMaterials.addButton")}
              </Button>
            </>
          )}
        </Flex>
      }
    >
      {/* No `selectionMode`: the edit row emits a fixed list of `<td>`s and
          never reads `children`, so it would not gain the checkbox cell the
          header and data rows do and every field would sit one column left of
          its heading. Porting the row to `buildEditRowCells` comes first. */}
      <EntityTable<SupplierMaterial>
        surface={TABLE_SURFACES.supplierMaterials}
        tab={DEFAULT_TAB}
        lockColumnLayout
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        loading={fetching && !materials.length}
        size="small"
        scroll={{ x: "max-content" }}
        withOutPagination
        pagination={false}
        className="m-0 [&_.ant-table]:m-0"
        onRow={(record) => ({
          onDoubleClick:
            canEdit && record.id !== NEW_ROW_ID
              ? () => {
                  if (isLocked || deletingId) return
                  setEditingId(record.id)
                }
              : undefined
        })}
        components={canEdit ? { body: { row: Row } } : {}}
        locale={{
          emptyText: loadFailed
            ? t("supplierMaterials.loadError")
            : search
              ? t("supplierMaterials.noMatches")
              : t("supplierMaterials.none")
        }}
      />

      {nextPage && (
        <Flex justify="center" className="pt-3">
          <Button
            type="link"
            loading={fetching}
            onClick={() => void loadPage(page + 1, { append: true })}
          >
            {t("supplierMaterials.loadMore", {
              loaded: materials.length,
              total: count
            })}
          </Button>
        </Flex>
      )}

      <Flex gap={8} align="flex-start" className="pt-3">
        <InfoCircleOutlined className="mt-1 shrink-0 text-gray-400" />
        <Text type="secondary" className="text-xs">
          {t("supplierMaterials.costExplainer")}
        </Text>
      </Flex>

      {pasting && (
        <MaterialPasteModal
          supplierId={supplierId}
          supplierName={supplierName}
          onClose={() => setPasting(false)}
          onImported={() => {
            setPasting(false)
            refresh()
          }}
        />
      )}
    </Card>
  )
}
